import { useState } from 'react';
import { useWalletBalance, useWalletStatement, useWithdrawals } from '@/hooks/wallet';
import { walletApi } from '@/api/wallet-api';
import { useQuery } from '@tanstack/react-query';
import { WalletBalanceCard } from '@/components/wallet/WalletBalanceCard';
import { WalletTransactionTable } from '@/components/wallet/WalletTransactionTable';
import { WithdrawalDialog } from '@/components/wallet/WithdrawalDialog';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Plus, History, RefreshCw, Key, ShieldCheck, Info } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { cn } from '@/utils/cn';

export function WalletPage() {
  const location = useLocation();
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR' || location.pathname.startsWith('/medico');
  const extratoPath = isDoctor ? '/medico/carteira/extrato' : '/carteira/extrato';
  const saquesPath = isDoctor ? '/medico/carteira/saques' : '/carteira/saques';
  const chavesPixPath = isDoctor ? '/medico/carteira/chaves-pix' : '/carteira/chaves-pix';

  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance, isFetching: balanceFetching } = useWalletBalance();
  const { data: statement, isLoading: statementLoading, refetch: refetchStatement } = useWalletStatement(1);
  const { data: withdrawals, isLoading: withdrawalsLoading, refetch: refetchWithdrawals } = useWithdrawals(1, 5);
  const { data: pixKey } = useQuery({
    queryKey: ['pixKeys'],
    queryFn: () => walletApi.getPixKey(),
    enabled: isDoctor,
  });

  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    await Promise.all([refetchBalance(), refetchStatement(), refetchWithdrawals()]);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  if (balanceLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Carteira</h1>
          <p className="text-sm text-gray-500 mt-1">Saldo, repasses de consultas e extrato financeiro</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            disabled={isRefreshing || balanceFetching}
            className="rounded-xl border-gray-200"
            title="Atualizar saldo e extrato"
          >
            <RefreshCw size={15} className={cn('mr-1.5', (isRefreshing || balanceFetching) && 'animate-spin')} />
            Atualizar
          </Button>
          <Button
            variant="primary"
            onClick={() => setWithdrawOpen(true)}
            disabled={!balance?.available || balance.available <= 0}
            className="rounded-xl font-semibold shadow-sm"
          >
            <Plus size={16} className="mr-1.5" />
            Sacar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <WalletBalanceCard type="available" amount={balance?.available ?? 0} />
        <WalletBalanceCard type="pending" amount={balance?.pending ?? 0} />
        <WalletBalanceCard type="blocked" amount={balance?.blocked ?? 0} />
      </div>

      {Number(balance?.pending ?? 0) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-amber-900 text-sm">
          <Info size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Saldo Pendente em Liberação</p>
            <p className="text-xs text-amber-800 mt-0.5">
              Os repasses de consultas realizadas são liberados para a conta disponível automaticamente após a confirmação do atendimento e período de segurança de 48 horas.
            </p>
          </div>
        </div>
      )}

      {/* Card informativo de Chave Pix do Médico */}
      {isDoctor && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10 text-primary border border-primary/20 shrink-0">
                <Key size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 text-sm">Chave PIX para Recebimento</h3>
                  {pixKey?.verifiedAt && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                      <ShieldCheck size={12} /> Verificada
                    </span>
                  )}
                </div>
                {pixKey ? (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Tipo: <strong className="text-gray-700">{pixKey.type}</strong> • Chave: <strong className="text-gray-900 font-mono">{pixKey.maskedKey}</strong>
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Nenhuma chave Pix cadastrada. Cadastre sua chave para solicitar transferências bancárias.
                  </p>
                )}
              </div>
            </div>
            <Link
              to={chavesPixPath}
              className="text-xs font-semibold text-primary hover:text-primary-hover px-3 py-1.5 rounded-lg border border-primary/20 hover:bg-primary/5 transition-colors self-start sm:self-auto shrink-0"
            >
              {pixKey ? 'Alterar Chave PIX' : 'Cadastrar Chave PIX'}
            </Link>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Extrato</h2>
          <Link
            to={extratoPath}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Ver todos
          </Link>
        </div>
        <div className="p-6">
          <WalletTransactionTable
            transactions={statement?.items ?? []}
            isLoading={statementLoading}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History size={16} className="text-gray-400" />
            <h2 className="font-semibold text-gray-900">Saques Recentes</h2>
          </div>
          <Link
            to={saquesPath}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Ver todos
          </Link>
        </div>
        <div className="p-6">
          {withdrawalsLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-10 rounded-lg" />
              ))}
            </div>
          ) : (withdrawals?.items?.length ?? 0) === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              Nenhum saque realizado
            </p>
          ) : (
            <div className="space-y-2">
              {withdrawals?.items?.map((w) => (
                <div
                  key={w.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(w.amount)}</p>
                    <p className="text-xs text-gray-500">{formatDate(w.createdAt)}</p>
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium px-2 py-0.5 rounded-full',
                      w.status === 'APPROVED'
                        ? 'bg-green-100 text-green-700'
                        : w.status === 'PENDING'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    )}
                  >
                    {w.status === 'APPROVED' ? 'Concluído' : w.status === 'PENDING' ? 'Pendente' : 'Falhou'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <WithdrawalDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        availableBalance={balance?.available ?? 0}
      />
    </div>
  );
}
